import UserManage from '../UserManage';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useToken from '@hooks/useToken';
import { apiRoute, requestSecureDelete, requestSecureGet } from '@libs/api';
import { useNavigate } from 'react-router-dom';
import { NoColumn } from '@components/Common/CTable/components/IdxColumn';
import CheckBoxContainer from '@components/Common/CInput/containers/CheckBoxContainer';
import { TableColumnTypes } from '@typedef/components/Common/CTable/table.column.type';
import { UserTypes } from '@typedef/components/UserManage/user.types';

const UserManageContainer = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useToken();
  const [tableData, setTableData] = useState<UserTypes[]>([]);
  const [selectRows, setSelectRows] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 체크박스 선택
  const onChangeCheckBox = useCallback(
    (idx: number) => {
      if (selectRows.includes(idx)) {
        const filter = selectRows.filter((row) => row !== idx);
        return setSelectRows(filter);
      }
      setSelectRows((row) => [...row, idx]);
    },
    [selectRows],
  );

  // 사용자 관리 (등록, 수정)
  const manage = useCallback(
    (type: 'insert' | 'update') => {
      let url = `/user-info?type=${type}`;
      if (type === 'update') {
        if (selectRows.length > 1) {
          alert('수정할 정보를 하나만 선택해 주세요.');
          return;
        } else if (selectRows.length === 0) {
          alert('수정할 정보를 선택해 주세요.');
          return;
        }
        url += `&userId=${selectRows[0]}`;
      }
      navigate(url);
    },
    [navigate, selectRows],
  );

  // 사용자 삭제
  const deleteUserData = useCallback(async () => {
    if (selectRows.length === 0) {
      alert('삭제할 데이터를 선택해 주세요.');
      return;
    }
    if (window.confirm('삭제하시겠습니까?')) {
      await Promise.all(
        selectRows.map(
          (idx) =>
            new Promise((resolve, reject) => {
              requestSecureDelete(
                apiRoute.user.deleteUser + idx,
                {},
                getAccessToken()!,
              ).then((data) => {
                if (data.config.status >= 200 && data.config.status < 400) {
                  resolve(data);
                } else {
                  reject('http request failed with ' + data.config.status);
                }
              });
            }),
        ),
      )
        .then(() => {
          alert('성공적으로 삭제가 완료되었습니다.');
          window.location.reload();
        })
        .catch(() => {
          alert('삭제 실패');
        });
    }
  }, [getAccessToken, selectRows]);

  // 테이블 검색
  const searchTable = useCallback(() => {
    if (inputRef.current) {
      setSearchValue(inputRef.current.value);
    }
  }, []);

  // 테이블 검색내역 삭제
  const clearSearchValue = useCallback(() => {
    const { current } = inputRef;
    if (current) {
      current.value = '';
    }
    setSearchValue(null);
  }, []);

  const columns: TableColumnTypes[] = useMemo(
    () => [
      {
        Header: '',
        accessor: 'checkbox',
        disableSortBy: true,
        width: 50,
        Cell: ({ row }: { row: any }) => {
          return (
            <CheckBoxContainer
              id={row.original.idx}
              defaultChecked={selectRows.includes(row.original.idx)}
              onChangeFunc={onChangeCheckBox}
            />
          );
        },
      },
      NoColumn('idx'),
      {
        Header: '소속',
        accessor: 'belong.name',
      },
      {
        Header: '사용자명',
        accessor: 'name',
      },
      {
        Header: '직책',
        accessor: 'class',
      },
      {
        Header: '개인번호(군번)',
        accessor: 'serial',
      },
      {
        Header: '단말기',
        accessor: 'belong_device.device_info.device.pet_name',
      },
      {
        Header: '단말 일련번호',
        accessor: 'belong_device.serial_number',
      },
    ],
    [onChangeCheckBox, selectRows],
  );

  const getTableData = useCallback(async () => {
    const { config, data } = await requestSecureGet<UserTypes[]>(
      apiRoute.user.getUsers,
      {},
      getAccessToken()!,
    );
    if (config.status >= 200 && config.status < 400) setTableData(data);
  }, [getAccessToken]);

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  return (
    <UserManage
      columns={columns}
      tableData={tableData}
      manage={manage}
      deleteUserData={deleteUserData}
      inputRef={inputRef}
      searchValue={searchValue}
      searchTable={searchTable}
      clearSearchValue={clearSearchValue}
    />
  );
};

export default UserManageContainer;
